/**
 * PartnerWaitlist (V2.0.61) — Superadmin console for the partner
 * signup queue.
 *
 * Backend endpoints (already exist):
 *   GET  /api/superadmin/partner/waitlist        list + totals
 *   GET  /api/superadmin/partner/waitlist.csv    csv export
 *   POST /api/superadmin/partner/waitlist/{id}/invite  mark invited
 *
 * Design goals for this page:
 *   1. Show live totals at the top so the owner immediately sees pipeline
 *      health without scanning the table.
 *   2. Chip filters (Type, Status, Serious-only) that map 1:1 to the
 *      existing backend query params — no client-side filtering, so the
 *      counts always reflect what's actually in Mongo.
 *   3. One-click Invite per row — hits `/invite` and optimistically
 *      updates the row so the UI feels instant.
 *   4. CSV download button just streams the existing endpoint into a
 *      Blob so the owner can email batches to their operations team.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft,
    Handshake,
    ShieldCheck,
    DownloadSimple,
    PaperPlaneTilt,
    LinkedinLogo,
    ArrowClockwise,
    MagnifyingGlass,
} from "@phosphor-icons/react";

import api from "@/lib/api";
import { logError } from "@/lib/logger";


const TYPE_OPTIONS = [
    { value: "", label: "All types" },
    { value: "freelancer", label: "Freelancer" },
    { value: "small_agency", label: "Small agency" },
    { value: "hr_consultant", label: "HR consultant" },
];

const STATUS_OPTIONS = [
    { value: "", label: "All status" },
    { value: "waiting", label: "Waiting" },
    { value: "invited", label: "Invited" },
    { value: "activated", label: "Activated" },
];


export default function PartnerWaitlist() {
    const [items, setItems] = useState([]);
    const [totals, setTotals] = useState({ total: 0, waiting: 0, invited: 0, activated: 0 });
    const [loading, setLoading] = useState(true);
    const [invitingId, setInvitingId] = useState(null);
    const [partnerType, setPartnerType] = useState("");
    const [status, setStatus] = useState("");
    const [seriousOnly, setSeriousOnly] = useState(false);
    const [search, setSearch] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ limit: "200" });
            if (partnerType) params.set("partner_type", partnerType);
            if (status) params.set("status", status);
            if (seriousOnly) params.set("serious_only", "true");
            const r = await api.get(`/superadmin/partner/waitlist?${params.toString()}`);
            setItems(r.data.items || []);
            setTotals(r.data.totals || { total: 0, waiting: 0, invited: 0, activated: 0 });
        } catch (e) {
            logError("partner waitlist load", e);
            toast.error(e?.response?.data?.detail || "Failed to load waitlist");
        } finally {
            setLoading(false);
        }
    }, [partnerType, status, seriousOnly]);

    useEffect(() => { load(); }, [load]);

    // Client-side search on the fetched page — cheap because backend
    // already trimmed by status/type filters, so this only refines a
    // relatively small slice.
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return items;
        return items.filter((r) =>
            (r.name || "").toLowerCase().includes(q) ||
            (r.email || "").toLowerCase().includes(q) ||
            (r.phone || "").toLowerCase().includes(q) ||
            (r.city || "").toLowerCase().includes(q),
        );
    }, [items, search]);

    const invite = async (row, { force = false } = {}) => {
        if (row.invited_at && !force) return;
        const label = row.name || row.email;
        const msg = force
            ? `Re-send invite email to ${label}?\n\nThis will trigger another activation email — only do this if the partner said they didn't receive the first one.`
            : `Mark ${label} as INVITED?\n\nThis will:\n  • Stamp invited_at\n  • Send the batch-invite email with an activation link`;
        if (!window.confirm(msg)) return;
        setInvitingId(row.id);
        try {
            const qs = force ? "?force=true" : "";
            const r = await api.post(`/superadmin/partner/waitlist/${row.id}/invite${qs}`);
            setItems((prev) => prev.map((x) =>
                x.id === row.id ? {
                    ...x,
                    invited_at: r.data.invited_at,
                    status: "invited",
                    last_invite_email_at: r.data.email_sent ? new Date().toISOString() : x.last_invite_email_at,
                } : x,
            ));
            if (!row.invited_at) {
                setTotals((t) => ({ ...t, waiting: Math.max(0, t.waiting - 1), invited: t.invited + 1 }));
            }
            if (r.data.email_sent) {
                toast.success(`Invite email sent (${r.data.email_provider})`);
            } else {
                toast.warning("Marked invited, but email did NOT send — check backend logs");
            }
        } catch (e) {
            logError("invite partner", e);
            toast.error(e?.response?.data?.detail || "Invite failed");
        } finally {
            setInvitingId(null);
        }
    };

    const [reprovisioningId, setReprovisioningId] = useState(null);
    const reprovision = async (row) => {
        const label = row.name || row.email;
        if (!window.confirm(
            `Re-provision login for ${label}?\n\n` +
            `This will re-run partner user + workspace creation using the password they set in the wizard. ` +
            `Use this only when the partner reports they can't log in after finishing the wizard.`
        )) return;
        setReprovisioningId(row.id);
        try {
            const r = await api.post(`/superadmin/partner/waitlist/${row.id}/reprovision`);
            if (r.data?.user_id) {
                toast.success("Partner login provisioned — they can now sign in with their wizard password");
                setItems((prev) => prev.map((x) =>
                    x.id === row.id ? { ...x, user_id: r.data.user_id, activation_error: null } : x,
                ));
            } else {
                toast.warning("Reprovision returned no user_id — check backend logs");
            }
        } catch (e) {
            logError("reprovision partner", e);
            toast.error(e?.response?.data?.detail || "Reprovision failed");
        } finally {
            setReprovisioningId(null);
        }
    };


    const exportCsv = async () => {
        try {
            // Uses same filters as the current view so the CSV matches
            // what the owner is actually looking at.
            const params = new URLSearchParams();
            if (partnerType) params.set("partner_type", partnerType);
            if (status) params.set("status", status);
            if (seriousOnly) params.set("serious_only", "true");
            const r = await api.get(`/superadmin/partner/waitlist.csv?${params.toString()}`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `partner-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            logError("export csv", e);
            toast.error("CSV export failed");
        }
    };

    const fmtDate = (iso) => {
        if (!iso) return "";
        try {
            return new Date(iso).toLocaleString("en-IN", {
                day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
            });
        } catch { return iso.slice(0, 16); }
    };

    return (
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-5 sm:py-8 overflow-x-hidden" data-testid="superadmin-partner-waitlist-page">
            <div className="mb-5 sm:mb-6">
                <Link
                    to="/superadmin"
                    data-testid="partner-waitlist-back"
                    className="text-[11px] font-black uppercase tracking-wider text-[#525252] hover:text-ink inline-flex items-center gap-1.5 mb-3"
                >
                    <ArrowLeft size={12} weight="bold" /> Superadmin
                </Link>
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-splash mb-1 inline-flex items-center gap-1.5">
                    <ShieldCheck size={12} weight="fill" /> Platform-level access
                </p>
                <h1 className="font-heading text-2xl sm:text-4xl font-black tracking-tight flex items-center gap-2 flex-wrap">
                    <Handshake size={24} weight="bold" className="sm:hidden" />
                    <Handshake size={28} weight="bold" className="hidden sm:inline" />
                    <span>Partner Waitlist</span>
                </h1>
                <p className="text-xs sm:text-sm text-[#525252] mt-2 max-w-2xl">
                    Every partner-program signup from <code className="text-[11px] bg-[#F5F5F5] px-1">/partner</code>. Filter, export, and mark rows as invited once you've sent the batch email.
                </p>
            </div>

            {/* Totals strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-5">
                {[
                    { key: "total", label: "Total signups", accent: "text-ink" },
                    { key: "waiting", label: "Waiting", accent: "text-amber-600" },
                    { key: "invited", label: "Invited", accent: "text-blue-600" },
                    { key: "activated", label: "Activated", accent: "text-emerald-600" },
                ].map((k) => (
                    <div key={k.key} className="bg-white border border-[#E5E5E5] p-4" data-testid={`partner-total-${k.key}`}>
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">{k.label}</p>
                        <p className={`font-heading font-black text-3xl sm:text-4xl tracking-tighter ${k.accent}`}>
                            {totals[k.key] || 0}
                        </p>
                    </div>
                ))}
            </div>

            {/* Filter row */}
            <div className="bg-white border border-[#E5E5E5] p-3 sm:p-4 mb-4 grid grid-cols-2 sm:flex sm:flex-wrap sm:items-center gap-2">
                <select
                    value={partnerType}
                    onChange={(e) => setPartnerType(e.target.value)}
                    data-testid="partner-filter-type"
                    className="border border-[#E5E5E5] px-2 sm:px-3 py-2 text-[11px] sm:text-xs font-bold uppercase tracking-wider min-w-0"
                >
                    {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    data-testid="partner-filter-status"
                    className="border border-[#E5E5E5] px-2 sm:px-3 py-2 text-[11px] sm:text-xs font-bold uppercase tracking-wider min-w-0"
                >
                    {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <label className="col-span-2 sm:col-auto inline-flex items-center gap-2 text-[11px] sm:text-xs font-bold uppercase tracking-wider px-3 py-2 border border-[#E5E5E5] cursor-pointer">
                    <input
                        type="checkbox"
                        checked={seriousOnly}
                        onChange={(e) => setSeriousOnly(e.target.checked)}
                        data-testid="partner-filter-serious"
                    />
                    Serious only
                </label>
                <div className="col-span-2 sm:flex-1 sm:min-w-[180px] relative">
                    <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search name, email, phone, city"
                        data-testid="partner-search"
                        className="w-full border border-[#E5E5E5] pl-8 pr-3 py-2 text-sm"
                    />
                </div>
                <button
                    type="button"
                    onClick={load}
                    disabled={loading}
                    data-testid="partner-refresh"
                    className="border border-[#E5E5E5] px-2 sm:px-3 py-2 text-[11px] sm:text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1.5 hover:bg-[#F5F5F5] disabled:opacity-40"
                >
                    <ArrowClockwise size={12} weight="bold" /> Refresh
                </button>
                <button
                    type="button"
                    onClick={exportCsv}
                    data-testid="partner-export-csv"
                    className="bg-[#0A0A0A] text-white px-2 sm:px-3 py-2 text-[11px] sm:text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1.5 hover:bg-[#1F2937]"
                >
                    <DownloadSimple size={12} weight="bold" /> Export
                </button>
            </div>

            {loading && (
                <p className="text-sm text-[#A3A3A3]" data-testid="partner-waitlist-loading">Loading…</p>
            )}

            {!loading && filtered.length === 0 && (
                <div className="bg-white border border-dashed border-[#E5E5E5] p-8 text-center" data-testid="partner-waitlist-empty">
                    <Handshake size={28} weight="bold" className="mx-auto text-[#A3A3A3] mb-3" />
                    <p className="font-bold text-ink mb-1">No partner signups match these filters</p>
                    <p className="text-sm text-[#525252]">
                        Try clearing filters or check back after your next FB / LinkedIn boost.
                    </p>
                </div>
            )}

            {!loading && filtered.length > 0 && (
                <>
                    {/* Mobile: card list. Table doesn't fit at 390px and
                        horizontal scroll on ops pages is a UX foot-gun. */}
                    <div className="sm:hidden space-y-2" data-testid="partner-waitlist-cards">
                        {filtered.map((row) => (
                            <div key={row.id} className="bg-white border border-[#E5E5E5] p-3" data-testid={`partner-card-${row.id}`}>
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                        <div className="font-bold text-ink flex items-center gap-1.5 flex-wrap">
                                            <span className="truncate">{row.name || "(no name)"}</span>
                                            {row.serious_recruiter && (
                                                <span className="bg-emerald-100 text-emerald-800 text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5">5+ placed</span>
                                            )}
                                        </div>
                                        <div className="text-xs text-[#525252] mt-0.5 truncate">{row.email}</div>
                                        <div className="text-xs text-[#525252]">{row.phone}</div>
                                        <div className="text-[11px] text-[#525252] mt-1">
                                            <span className="capitalize">{(row.partner_type || "—").replace("_", " ")}</span>
                                            {row.city ? ` · ${row.city}` : ""}
                                        </div>
                                    </div>
                                    <StatusPill status={row.status} invitedAt={row.invited_at} activatedAt={row.activated_at} />
                                </div>
                                <div className="text-[10px] text-[#A3A3A3] mt-2">Signed up {fmtDate(row.created_at)}</div>
                                <div className="mt-3">
                                    {row.invited_at ? (
                                        <div className="flex items-center gap-3">
                                            <span className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3]">Invited {fmtDate(row.invited_at)}</span>
                                            {row.last_invite_email_at && (
                                                <span className="text-[10px] text-emerald-700 inline-flex items-center gap-1">
                                                    <PaperPlaneTilt size={10} weight="bold" /> sent
                                                </span>
                                            )}
                                            <button type="button" onClick={() => invite(row, { force: true })} disabled={invitingId === row.id}
                                                    className="ml-auto text-[10px] font-black uppercase tracking-wider text-[#0891B2] disabled:opacity-40">
                                                {invitingId === row.id ? "Sending…" : "Send again"}
                                            </button>
                                        </div>
                                    ) : (
                                        <button type="button" onClick={() => invite(row)} disabled={invitingId === row.id}
                                                data-testid={`partner-invite-mobile-${row.id}`}
                                                className="w-full bg-[#2952F5] text-white py-2 text-[11px] font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1 disabled:opacity-40">
                                            <PaperPlaneTilt size={11} weight="bold" />
                                            {invitingId === row.id ? "Sending…" : "Send invite"}
                                        </button>
                                    )}
                                </div>
                                {row.status === "activated" && (
                                    <div className="mt-3 border border-amber-300 bg-amber-50 p-2 space-y-2">
                                        <p className="text-[10px] font-bold text-amber-900">
                                            {row.activation_error
                                                ? row.activation_error
                                                : !row.user_id
                                                    ? "Login not provisioned yet — click below to heal."
                                                    : "Can't sign in? Re-run provisioning with the wizard password."}
                                        </p>
                                        <button type="button" onClick={() => reprovision(row)}
                                                disabled={reprovisioningId === row.id}
                                                data-testid={`partner-reprovision-mobile-${row.id}`}
                                                className="w-full bg-amber-600 text-white py-1.5 text-[10px] font-black uppercase tracking-wider disabled:opacity-40">
                                            {reprovisioningId === row.id ? "Fixing…" : "Fix login"}
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Desktop table — hidden on mobile */}
                    <div className="hidden sm:block bg-white border border-[#E5E5E5] overflow-x-auto" data-testid="partner-waitlist-table">
                    <table className="w-full text-sm min-w-[900px]">
                        <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                            <tr className="text-left">
                                {["Partner", "Type", "Location", "Signed up", "Status", ""].map((h) => (
                                    <th key={h} className="px-4 py-3 text-[10px] font-black uppercase tracking-wider text-[#525252]">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((row) => (
                                <tr key={row.id} className="border-b border-[#F5F5F5] last:border-b-0 hover:bg-[#FAFAFA]"
                                    data-testid={`partner-row-${row.id}`}>
                                    <td className="px-4 py-3">
                                        <div className="font-bold text-ink flex items-center gap-2">
                                            {row.name || "(no name)"}
                                            {row.serious_recruiter && (
                                                <span className="bg-emerald-100 text-emerald-800 text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5">
                                                    5+ placed
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-xs text-[#525252] mt-0.5">{row.email}</div>
                                        <div className="text-xs text-[#525252]">{row.phone}</div>
                                        {row.linkedin_url && (
                                            <a href={row.linkedin_url} target="_blank" rel="noreferrer noopener"
                                               className="text-xs text-[#0A66C2] hover:underline inline-flex items-center gap-1 mt-0.5">
                                                <LinkedinLogo size={11} weight="bold" /> LinkedIn
                                            </a>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-xs">
                                        <span className="capitalize">{(row.partner_type || "—").replace("_", " ")}</span>
                                    </td>
                                    <td className="px-4 py-3 text-xs text-[#525252]">{row.city || "—"}</td>
                                    <td className="px-4 py-3 text-xs text-[#525252] whitespace-nowrap">{fmtDate(row.created_at)}</td>
                                    <td className="px-4 py-3">
                                        <StatusPill status={row.status} invitedAt={row.invited_at} activatedAt={row.activated_at} />
                                    </td>
                                    <td className="px-4 py-3">
                                        {row.invited_at ? (
                                            <div className="flex flex-col gap-1 items-start">
                                                <span className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3]">
                                                    Invited {fmtDate(row.invited_at)}
                                                </span>
                                                {row.last_invite_email_at && (
                                                    <span className="text-[10px] text-emerald-700 inline-flex items-center gap-1">
                                                        <PaperPlaneTilt size={10} weight="bold" /> email sent
                                                    </span>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => invite(row, { force: true })}
                                                    disabled={invitingId === row.id}
                                                    data-testid={`partner-resend-${row.id}`}
                                                    className="text-[10px] font-black uppercase tracking-wider text-[#0891B2] hover:text-[#0A0A0A] disabled:opacity-40"
                                                >
                                                    {invitingId === row.id ? "Sending…" : "Send again"}
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => invite(row)}
                                                disabled={invitingId === row.id}
                                                data-testid={`partner-invite-${row.id}`}
                                                className="bg-[#2952F5] text-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider inline-flex items-center gap-1 hover:bg-[#1e40af] disabled:opacity-40"
                                            >
                                                <PaperPlaneTilt size={11} weight="bold" />
                                                {invitingId === row.id ? "Sending…" : "Send invite"}
                                            </button>
                                        )}
                                        {row.status === "activated" && (
                                            <div className="mt-2 border border-amber-300 bg-amber-50 px-2 py-1.5 space-y-1 max-w-[200px]">
                                                <p className="text-[9px] font-bold text-amber-900 leading-tight">
                                                    {row.activation_error
                                                        ? row.activation_error
                                                        : !row.user_id
                                                            ? "Login not provisioned"
                                                            : "Can't sign in? Re-run"}
                                                </p>
                                                <button
                                                    type="button"
                                                    onClick={() => reprovision(row)}
                                                    disabled={reprovisioningId === row.id}
                                                    data-testid={`partner-reprovision-${row.id}`}
                                                    className="w-full bg-amber-600 text-white py-1 text-[10px] font-black uppercase tracking-wider hover:bg-amber-700 disabled:opacity-40"
                                                >
                                                    {reprovisioningId === row.id ? "Fixing…" : "Fix login"}
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                </>
            )}
        </div>
    );
}

function StatusPill({ status, invitedAt, activatedAt }) {
    let label = status || "waiting";
    let cls = "bg-amber-100 text-amber-800";
    if (status === "invited" || invitedAt) { label = "invited"; cls = "bg-blue-100 text-blue-800"; }
    if (status === "activated" || activatedAt) { label = "activated"; cls = "bg-emerald-100 text-emerald-800"; }
    return (
        <span className={`${cls} text-[10px] font-black uppercase tracking-wider px-2 py-1`}>
            {label}
        </span>
    );
}
